import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createDefaultTranslationSettings } from '@talk2nick/core';

import type { AuthState } from '../features/auth/useAuth';
import { useTranslationUsage } from '../features/translate/usage';

/** 경고 기준 (PLAN 5-7-5장: 80%에서 노란색/배너) */
const WARN_RATIO = 0.8;
/** 하루 한 번만 띄운다 — localStorage에 '날짜:단계'를 남긴다 */
const NOTICE_KEY = 't2n:translationNotice';

type Level = 'warn' | 'reached';

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function alreadyNotified(level: Level): boolean {
  try {
    const raw = window.localStorage.getItem(NOTICE_KEY);
    if (!raw) return false;
    const [day, seen] = raw.split(':');
    if (day !== todayKey()) return false;
    // 이미 더 강한(도달) 알림을 봤으면 경고는 다시 띄우지 않는다
    return seen === level || seen === 'reached';
  } catch {
    return false;
  }
}

function markNotified(level: Level): void {
  try {
    window.localStorage.setItem(NOTICE_KEY, `${todayKey()}:${level}`);
  } catch {
    // 사파리 프라이빗 모드 등 localStorage 실패는 무시한다
  }
}

/**
 * 번역 한도 안내 배너 (PLAN 5-7-5장).
 *
 * 80%에서 노란 배너, 100%에서 빨간 배너를 **하루 한 번** 띄운다. 설정 화면의
 * 진행바와 같은 스토어(`useTranslationUsage`)를 보므로 숫자가 어긋나지 않는다.
 * 100%가 되면 자동 번역은 멈추고(대화방이 `reached`를 보고 큐를 비운다)
 * 버블에는 \"직접 번역\" 링크만 남는다.
 */
export function TranslationLimitBanner({ auth }: { auth: AuthState }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const uid = auth.firebaseUser?.uid ?? null;
  const settings = auth.profile?.settings?.translation ?? createDefaultTranslationSettings('ko');
  const usage = useTranslationUsage(uid, settings.dailyCharLimit);
  const [hidden, setHidden] = useState(false);

  const level: Level | null = usage.reached ? 'reached' : usage.ratio >= WARN_RATIO ? 'warn' : null;

  if (!level || hidden || !usage.limit || alreadyNotified(level)) return null;

  const message =
    level === 'reached'
      ? t('translate.limitReached')
      : t('translate.limitWarning', { percent: Math.round(usage.ratio * 100) });

  return (
    <div
      role="status"
      aria-live="polite"
      className="inapp-banner inapp-banner--up fixed inset-x-0 bottom-[calc(var(--safe-bottom)+var(--space-3))] z-[var(--z-banner)] px-3"
    >
      <div
        className="mx-auto flex w-full max-w-[480px] items-center gap-3 rounded-[var(--radius-lg)] bg-[var(--bg-list)] px-3 py-2.5"
        style={{
          borderInlineStart: `3px solid ${level === 'reached' ? 'var(--danger)' : 'var(--warning)'}`,
        }}
      >
        <span className="min-w-0 flex-1 text-xs">{message}</span>
        <button
          type="button"
          className="pressable shrink-0 text-xs font-medium text-[var(--accent)]"
          onClick={() => {
            markNotified(level);
            setHidden(true);
            navigate('/settings/translation');
          }}
        >
          {t('translate.openSettings')}
        </button>
        <button
          type="button"
          className="pressable shrink-0 text-xs text-[var(--text-secondary)]"
          aria-label={t('common.close')}
          onClick={() => {
            markNotified(level);
            setHidden(true);
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
